import {
  CreateScheduleInput,
  DayProfileType,
  ScheduleExceptionDataInput,
  ScheduleHourlyProfileDataInput,
  ScheduleMonthlyProfileDataInput,
  ScheduleWeeklyProfileDataInput,
  ScheduleFragment,
  UpdateScheduleInput,
} from '../../../generated/graphql'
import { i18n } from '../../../i18n'
import { parseDecimal } from '../../../util/parsers'

export type FormValues = {
  name: string
  hourlyNormals: string[]
  hourlyExceptions: string[]
  weekdayValues: DayProfileType[]
  monthlyValues: string[]
  exceptionInUse: boolean
  exceptionStart?: Date
  exceptionEnd?: Date
  exceptionProfile: DayProfileType
}

export const mapFormValuesToInput = ({
  name,
  hourlyNormals,
  hourlyExceptions,
  weekdayValues,
  monthlyValues,
  exceptionInUse,
  exceptionStart,
  exceptionEnd,
  exceptionProfile,
}: FormValues): CreateScheduleInput => {
  const hourlyNormal: ScheduleHourlyProfileDataInput = {
    profileType: DayProfileType.Normal,
    values: hourlyNormals.map((i) => parseDecimal(i.toString())),
  }

  const hourlyException: ScheduleHourlyProfileDataInput = {
    profileType: DayProfileType.Exception,
    values: hourlyExceptions.map((i) => parseDecimal(i.toString())),
  }

  const weeklyData: ScheduleWeeklyProfileDataInput = {
    profileTypes: weekdayValues.map(
      (p) => DayProfileType[p as keyof typeof DayProfileType],
    ),
  }

  const monthlyData: ScheduleMonthlyProfileDataInput = {
    multipliers: monthlyValues.map((i) => parseDecimal(i.toString())),
  }

  const getIsoStringAtUTCTimeOfDay = (
    hour: number,
    date?: Date,
  ): string | undefined => {
    if (!date) {
      return date
    }

    const modifiedDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
    )
    modifiedDate.setUTCHours(hour, 0, 0, 0)
    return modifiedDate.toISOString()
  }

  const exceptionInput: ScheduleExceptionDataInput = {
    inUse: exceptionInUse,
    startAt: getIsoStringAtUTCTimeOfDay(0, exceptionStart),
    endAt: getIsoStringAtUTCTimeOfDay(23, exceptionEnd),
    profileType: exceptionProfile,
  }

  return {
    name,
    hourlyProfiles: [hourlyNormal, hourlyException],
    weeklyProfile: weeklyData,
    monthlyProfile: monthlyData,
    exceptionData: exceptionInput,
  }
}

export const mapFormValuesToInputForCalibration = ({
  name,
  hourlyNormals,
  monthlyValues,
}: FormValues): UpdateScheduleInput => {
  const hourlyNormal: ScheduleHourlyProfileDataInput = {
    profileType: DayProfileType.Normal,
    values: hourlyNormals.map((i) => parseDecimal(i.toString())),
  }

  const hourlyException: ScheduleHourlyProfileDataInput = {
    profileType: DayProfileType.Exception,
    values: [],
  }

  const weeklyData: ScheduleWeeklyProfileDataInput = {
    profileTypes: [],
  }

  const monthlyData: ScheduleMonthlyProfileDataInput = {
    multipliers: monthlyValues.map((i) => parseDecimal(i.toString())),
  }

  const exceptionInput: ScheduleExceptionDataInput =
    {} as ScheduleExceptionDataInput

  return {
    name,
    hourlyProfiles: [hourlyNormal, hourlyException],
    weeklyProfile: weeklyData,
    monthlyProfile: monthlyData,
    exceptionData: exceptionInput,
  }
}

export const mapInputToFormValues = ({
  name,
  hourlyProfiles,
  weeklyProfile,
  monthlyProfile,
  exceptionData,
}: ScheduleFragment): FormValues => ({
  name,
  hourlyNormals:
    hourlyProfiles
      ?.find((p) => p.profileType === DayProfileType.Normal)
      ?.values.map(String) ?? [],
  hourlyExceptions:
    hourlyProfiles
      ?.find((p) => p.profileType === DayProfileType.Exception)
      ?.values.map(String) ?? [],
  weekdayValues: weeklyProfile?.profileTypes ?? [],
  monthlyValues: monthlyProfile?.multipliers.map(String) ?? [],
  exceptionInUse: exceptionData?.inUse ?? false,
  exceptionStart: exceptionData?.startAt
    ? new Date(exceptionData.startAt.replace('Z', ''))
    : undefined,
  exceptionEnd: exceptionData?.endAt
    ? new Date(exceptionData.endAt.replace('Z', ''))
    : undefined,
  exceptionProfile: exceptionData?.profileType ?? DayProfileType.Off,
})

export const defaultValues: FormValues = {
  name: '',
  hourlyNormals: Array<string>(24).fill('0'),
  hourlyExceptions: Array<string>(24).fill('0'),
  weekdayValues: Array<DayProfileType>(7).fill(DayProfileType.Normal),
  monthlyValues: Array<string>(12).fill('1'),
  exceptionInUse: false,
  exceptionStart: undefined,
  exceptionEnd: undefined,
  exceptionProfile: DayProfileType.Off,
}

export const defaultValuesForAlways: FormValues = {
  name: i18n.t('schedule.always'),
  hourlyNormals: Array<string>(24).fill('1'),
  hourlyExceptions: Array<string>(24).fill('0'),
  weekdayValues: Array<DayProfileType>(7).fill(DayProfileType.Always),
  monthlyValues: Array<string>(12).fill('1'),
  exceptionInUse: false,
  exceptionStart: undefined,
  exceptionEnd: undefined,
  exceptionProfile: DayProfileType.Off,
}

export const defaultValuesForNever: FormValues = {
  name: i18n.t('schedule.never'),
  hourlyNormals: Array<string>(24).fill('0'),
  hourlyExceptions: Array<string>(24).fill('0'),
  weekdayValues: Array<DayProfileType>(7).fill(DayProfileType.Normal),
  monthlyValues: Array<string>(12).fill('0'),
  exceptionInUse: false,
  exceptionStart: undefined,
  exceptionEnd: undefined,
  exceptionProfile: DayProfileType.Off,
}
